import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_CONFIG } from '../config';
import {
  ensureSessionWithRefresh,
  handleRefreshFailure,
} from './authSession';

// Simple in-memory cache and in-flight request deduplication
const responseCache = new Map(); // key -> { timestamp, data }
const inflight = new Map(); // key -> Promise
const CACHE_TTL_MS = 60 * 1000; // 1 minute cache for GETs
const INSTALLS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minute cache for installs data
// Staged-items queries hit an ever-growing DynamoDB GSI partition (every item ever
// staged, approved or not, lives in it - see adminOperations.mjs getStagedItems) and are
// expensive; the list doesn't need to be second-fresh, so cache longer than the default.
const STAGED_ITEMS_CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes

const getCacheKey = (url) => `GET:${url}`;

const getCachedOrInflight = (url, fetcher, customTTL = null) => {
  const key = getCacheKey(url);
  const ttl = customTTL || CACHE_TTL_MS;

  // Serve fresh cache
  const cached = responseCache.get(key);
  if (cached && Date.now() - cached.timestamp < ttl) {
    return Promise.resolve(cached.data);
  }

  // Deduplicate concurrent requests
  const existing = inflight.get(key);
  if (existing) return existing;

  const promise = fetcher().then((data) => {
    responseCache.set(key, { timestamp: Date.now(), data });
    inflight.delete(key);
    return data;
  }).catch((err) => {
    inflight.delete(key);
    throw err;
  });

  inflight.set(key, promise);
  return promise;
};

// Clear the response cache (useful when data is updated and we need fresh results)
export const clearResponseCache = () => {
  responseCache.clear();
  inflight.clear();
};

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate axios instance for public API endpoints
const publicApiClient = axios.create({
  baseURL: API_CONFIG.publicBaseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for debugging and CORS handling
apiClient.interceptors.request.use(
  (config) => {
    // CORS Preflight Note:
    // When making POST/GET requests with custom headers (like Authorization), browsers automatically
    // send an OPTIONS preflight request first. The browser does NOT include custom headers in the
    // OPTIONS request - this is automatic browser behavior. However, the server MUST:
    // 1. Accept OPTIONS requests without requiring authentication
    // 2. Return proper CORS headers (Access-Control-Allow-Origin, Access-Control-Allow-Methods, etc.)
    // 3. Include Access-Control-Allow-Headers: Authorization (and other custom headers)
    //
    // If CORS errors occur, it's typically because the server is trying to authenticate OPTIONS
    // requests or not returning proper CORS headers in the OPTIONS response.
    //
    // For OPTIONS requests (if axios were to make them directly), don't include auth headers
    if (config.method?.toUpperCase() === 'OPTIONS') {
      console.log('🔄 CORS preflight (OPTIONS) request detected, removing auth headers');
      delete config.headers.Authorization;
      // Also remove any other custom headers that might trigger preflight issues
      config.headers = {
        'Content-Type': 'application/json',
        ...config.headers
      };
    }
    
    console.log('🌐 Making HTTP request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      headers: config.headers,
      data: config.data
    });
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    if (
      status === 401
      && originalRequest
      && !originalRequest._authRetry
    ) {
      originalRequest._authRetry = true;
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const token = session.tokens?.idToken?.toString();
        if (token) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshErr) {
        await handleRefreshFailure(refreshErr);
      }
    }

    // Check for CORS errors specifically
    const isCorsError = !error.response && error.message && (
      error.message.includes('CORS') ||
      error.message.includes('Network Error') ||
      error.message.includes('Failed to fetch')
    );
    
    if (isCorsError) {
      console.error('🚫 CORS Error detected:', {
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
        fullURL: error.config ? `${error.config.baseURL}${error.config.url}` : 'unknown',
        note: 'This may be caused by the server not properly handling OPTIONS preflight requests'
      });
    }
    
    const enhancedError = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        method: error.config?.method,
        url: error.config?.url,
      },
      isCorsError,
    };

    // Log error for debugging
    console.error('API Error:', enhancedError);

    return Promise.reject(enhancedError);
  }
);

// Add interceptors for public API client too
publicApiClient.interceptors.request.use(
  (config) => {
    console.log('🌍 Making PUBLIC HTTP request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      headers: config.headers,
      data: config.data
    });
    return config;
  },
  (error) => {
    console.error('❌ Public API request interceptor error:', error);
    return Promise.reject(error);
  }
);

publicApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const enhancedError = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        method: error.config?.method,
        url: error.config?.url,
      },
    };

    console.error('❌ Public API Error:', enhancedError);
    return Promise.reject(enhancedError);
  }
);

// Centralized auth function
const getAuthHeaders = async () => {
  try {
    const session = await ensureSessionWithRefresh();
    const authTokens = session.tokens;
    if (!authTokens?.idToken) {
      throw new Error('Authentication failed. Please sign in again.');
    }
    return {
      Authorization: `Bearer ${authTokens.idToken.toString()}`
    };
  } catch (error) {
    console.error('Failed to get auth headers:', error);
    throw new Error('Authentication failed. Please sign in again.');
  }
};

// Helper to make authenticated requests
const makeAuthenticatedRequest = async (requestFn, providedAuthHeaders = null) => {
  try {
    // Use provided headers or get fresh ones
    const authHeaders = providedAuthHeaders || await getAuthHeaders();
    console.log('🔐 Making authenticated request with headers:', authHeaders);
    return await requestFn(authHeaders);
  } catch (error) {
    console.error('🚨 Authenticated request failed:', error);
    // If auth error, try to refresh and retry once
    if (error.status === 401 || error.status === 403) {
      throw new Error('Authentication failed. Please sign in again.');
    }
    throw error;
  }
};

// Standardized API response handler
const handleApiResponse = (response, options = {}) => {
  const { expectArray = false, expectSingle = false } = options;

  if (expectArray && response.data.items) {
    return response.data.items;
  }

  if (expectSingle && response.data.items) {
    return response.data.items[0];
  }

  return response.data;
};

export const fetchPlayground = async (id, authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.playgroundById(id);
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            return handleApiResponse(response, { expectSingle: true });
        });
    }, authHeaders);
};

export const fetchPlaygroundHistory = async (id, authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.history(id);
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            const items = handleApiResponse(response, { expectArray: true });
            return items.sort((a, b) => new Date(b.historyTimeStamp) - new Date(a.historyTimeStamp));
        });
    }, authHeaders);
};

export const savePlayground = async (playground, authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const response = await apiClient.post(API_CONFIG.endpoints.admin,
            JSON.stringify([playground]),
            { headers }
        );
        return handleApiResponse(response);
    }, authHeaders);
};

export const deletePlayground = async (id, authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const response = await apiClient.delete(API_CONFIG.endpoints.playgroundById(id), {
            headers
        });
        return handleApiResponse(response);
    }, authHeaders);
};

export const acceptChanges = async (id, authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const response = await apiClient.get(API_CONFIG.endpoints.accept(id), {
            headers
        });
        return handleApiResponse(response);
    }, authHeaders);
};

export const togglePlaygroundFlag = async (id, shouldFlag, authHeaders) => {
    if (!shouldFlag) {
        return makeAuthenticatedRequest(async (headers) => {
            const response = await apiClient.get(API_CONFIG.endpoints.unflag(id), {
                headers
            });
            return handleApiResponse(response);
        }, authHeaders);
    }
    // For flagging, we need to update the playground properties
    return null; // This will be handled by savePlayground
};

export const geocodePlayground = async (id, authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.geocodeById(id);
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            return handleApiResponse(response, { expectSingle: true });
        });
    }, authHeaders);
};

export const venturePlayground = async (geohash, authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.ventureByHash(geohash);
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            return handleApiResponse(response, { expectSingle: true });
        });
    }, authHeaders);
};

// Fetch staged items from staging table with optional status filtering
export const fetchStagedItems = async (status = 'none', authHeaders = null, options = {}) => {
    const { includeParent = false } = options;
    console.log('📋 Fetching staged items with status:', status, 'includeParent:', includeParent);
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.staged(status, includeParent);
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            console.log('✅ Staged items response:', response.data);
            // The response contains { items: [...] }
            return handleApiResponse(response, { expectArray: true });
        }, STAGED_ITEMS_CACHE_TTL_MS);
    }, authHeaders);
};

// Fetch advertising data
export const fetchAdvertising = async (authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.advertising;
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            return handleApiResponse(response, { expectArray: true });
        });
    }, authHeaders);
};

// Save or update advertising data
export const saveAdvertising = async (ad, authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const response = await apiClient.post(API_CONFIG.endpoints.advertising,
            JSON.stringify(ad),
            { headers }
        );
        return handleApiResponse(response);
    }, authHeaders);
};

/** Upload public sponsor creative or map icon (S3 ads/ or icons/). */
export const uploadPublicAsset = async ({ fileContent, contentType, kind, imageFit }, authHeaders = null) => {
    return makeAuthenticatedRequest(async (headers) => {
        const payload = { fileContent, contentType, kind };
        if (imageFit != null && String(imageFit).trim() !== '') {
            payload.imageFit = imageFit;
        }
        const response = await apiClient.post(
            API_CONFIG.endpoints.adminUpload,
            JSON.stringify(payload),
            { headers: { ...headers, 'Content-Type': 'application/json' } }
        );
        return handleApiResponse(response);
    }, authHeaders);
};

/** List existing public icons under icons/ prefix (for picker UI). */
export const fetchAdminIcons = async (authHeaders = null) => {
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.adminIcons;
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            const data = handleApiResponse(response);
            return Array.isArray(data?.icons) ? data.icons : [];
        }, 2 * 60 * 1000);
    }, authHeaders);
};

// Additional API methods for dashboard and search
export const fetchDashboardStats = async (authHeaders) => {
    console.log('📊 Fetching dashboard stats from:', API_CONFIG.endpoints.report);
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.report;
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            console.log('✅ Dashboard stats response:', response.data);
            return handleApiResponse(response);
        });
    }, authHeaders);
};

export const searchPlaygrounds = async (query, authHeaders) => {
    console.log('🔍 searchPlaygrounds called with query:', query, 'authHeaders:', authHeaders);
    console.log('🔗 Search endpoint URL:', API_CONFIG.endpoints.search(query));
    return makeAuthenticatedRequest(async (headers) => {
        console.log('🔍 About to make search request with headers:', headers);
        const url = API_CONFIG.endpoints.search(query);
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            console.log('✅ Search response:', response.data);
            return handleApiResponse(response, { expectArray: true });
        });
    }, authHeaders);
};

export const forceGeocode = async (authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const response = await apiClient.get(API_CONFIG.endpoints.geocode, {
            headers
        });
        return handleApiResponse(response);
    }, authHeaders);
};

export const fetchMapStats = async (authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.stats;
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            return handleApiResponse(response, { expectArray: true });
        });
    }, authHeaders);
};

export const fetchCoverageKml = async (authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const response = await apiClient.get(API_CONFIG.endpoints.coverageKml, {
            headers,
            responseType: 'text'
        });
        return response.data;
    }, authHeaders);
};

export const fetchInstalls = async (days = 7, authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.installs(days);
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            return handleApiResponse(response, { expectArray: true });
        });
    }, authHeaders);
};

export const fetchInstallSummary = async (authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.installSummary;
        const response = await apiClient.get(url, { headers });
        return handleApiResponse(response);
    }, authHeaders);
};

export const ventureByQuery = async (query, authHeaders) => {
    return makeAuthenticatedRequest(async (headers) => {
        const response = await apiClient.get(API_CONFIG.endpoints.venture(query), {
            headers
        });
        return handleApiResponse(response);
    }, authHeaders);
};

// Additional service functions for components that were making direct axios calls

// Slack historically deep-linked app submissions as `node/<uuid>` even though
// those rows are stored as bare UUIDs. Retry without the OSM-type prefix.
const alternateIdsForLookup = (id) => {
    const raw = id == null ? '' : String(id).trim();
    if (!raw) return [];
    const match = raw.match(/^(way|node|relation)\/(.+)$/i);
    if (!match) return [raw];
    return [raw, match[2]];
};

// Fetch playground with optional auth headers (centralized version)
export const fetchPlaygroundById = async (id, authHeaders = null) => {
    return makeAuthenticatedRequest(async (headers) => {
        let lastResult;
        for (const candidateId of alternateIdsForLookup(id)) {
            const response = await apiClient.get(API_CONFIG.endpoints.playgroundById(candidateId), {
                headers
            });
            lastResult = handleApiResponse(response, { expectSingle: true });
            if (lastResult) {
                return lastResult;
            }
        }
        return lastResult;
    }, authHeaders);
};

// Fetch playground history with optional auth headers (centralized version)
export const fetchPlaygroundHistoryById = async (id, authHeaders = null) => {
    return makeAuthenticatedRequest(async (headers) => {
        const response = await apiClient.get(API_CONFIG.endpoints.history(id), {
            headers
        });
        const items = handleApiResponse(response, { expectArray: true });
        return items.sort((a, b) => new Date(b.historyTimeStamp) - new Date(a.historyTimeStamp));
    }, authHeaders);
};

// Search playgrounds with optional auth headers (centralized version)
export const searchPlaygroundsByQuery = async (query, authHeaders = null) => {
    return makeAuthenticatedRequest(async (headers) => {
        const response = await apiClient.get(API_CONFIG.endpoints.search(query), {
            headers
        });
        return handleApiResponse(response, { expectArray: true });
    }, authHeaders);
};

// Save playground with optional auth headers (centralized version)
export const savePlaygroundData = async (playground, authHeaders = null) => {
    return makeAuthenticatedRequest(async (headers) => {
        const response = await apiClient.post(API_CONFIG.endpoints.admin,
            JSON.stringify([playground]),
            { headers }
        );
        return handleApiResponse(response);
    }, authHeaders);
};

// Search for nearby playgrounds by coordinates (DEPRECATED - uses admin endpoint with auth)
export const searchNearbyPlaygrounds = async (lat, lon, authHeaders = null) => {
    const query = `lat=${lat}&lon=${lon}`;
    console.log('🗺️ Searching for nearby playgrounds with query:', query);
    
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.search(query);
        return getCachedOrInflight(url, async () => {
            const response = await apiClient.get(url, { headers });
            console.log('✅ Nearby search response:', response.data);
            return handleApiResponse(response, { expectArray: true });
        });
    }, authHeaders);
};

// Search for nearby playgrounds using public API endpoint with API key
export const searchNearbyPlaygroundsPublic = async (lat, lon) => {
    console.log('🗺️ Searching for nearby playgrounds (public API) with coordinates:', { lat, lon });
    
    const url = API_CONFIG.endpoints.playground(lat, lon);
    console.log('🔗 Public playground search URL:', url);
    console.log('🏢 Using public API base URL:', API_CONFIG.publicBaseURL);
    
    return getCachedOrInflight(url, async () => {
        // Prepare headers with API key
        const headers = {
            'Content-Type': 'application/json'
        };
        
        if (API_CONFIG.apiKey) {
            headers['x-api-key'] = API_CONFIG.apiKey;
            console.log('🔑 Using API key for public playground search');
        } else {
            console.log('⚠️ No API key found, trying public endpoint without authentication');
        }
        
        // Use the public API client which points to test.api.bearswithapps.com
        const response = await publicApiClient.get(url, { headers });
        console.log('✅ Public nearby search response:', response.data);
        
        // Public API returns data in 'features' array, not 'items'
        if (response.data && Array.isArray(response.data.features)) {
            console.log(`🎯 Found ${response.data.features.length} nearby playgrounds from public API`);
            return response.data.features;
        } else {
            console.log('⚠️ Unexpected public API response format, returning empty array');
            return [];
        }
    });
}; 

// Fetch a single playground using the public API with API key
export const fetchPublicPlaygroundById = async (id) => {
    const url = `/playground/${encodeURIComponent(id)}`;
    console.log('🔗 Public playground detail URL:', url);
    console.log('🏢 Using public API base URL:', API_CONFIG.publicBaseURL);

    const headers = { 'Content-Type': 'application/json' };
    if (API_CONFIG.apiKey) {
        headers['x-api-key'] = API_CONFIG.apiKey;
        console.log('🔑 Using API key for public playground detail');
    } else {
        console.log('⚠️ No API key found for public playground detail');
    }

    try {
        const response = await publicApiClient.get(url, { headers });
        const data = response.data;
        
        // Verify the response actually contains playground data
        // A valid playground should have at least an id property
        if (!data || !data.id) {
            const error = new Error('Playground not found');
            error.status = 404;
            throw error;
        }
        
        return data;
    } catch (error) {
        // Ensure error has status property for proper handling
        if (error.response) {
            const enhancedError = new Error(error.response.statusText || 'Playground not found');
            enhancedError.status = error.response.status;
            throw enhancedError;
        }
        // If error already has status, re-throw as-is
        if (error.status) {
            throw error;
        }
        // Otherwise, wrap it
        const wrappedError = new Error(error.message || 'Failed to fetch playground');
        wrappedError.status = error.status || 500;
        throw wrappedError;
    }
};

// Flag a playground as duplicate using public API with API key (no user auth required)
export const flagPlaygroundAsDuplicate = async (id, duplicateOfId, userEmail = 'admin@bearswithapps.com') => {
    const url = API_CONFIG.endpoints.flagPlayground(id);
    console.log('🚩 Flagging playground as duplicate:', { id, duplicateOfId, url });
    console.log('🏢 Using public API base URL:', API_CONFIG.publicBaseURL);

    const headers = { 'Content-Type': 'application/json' };
    if (API_CONFIG.apiKey) {
        headers['x-api-key'] = API_CONFIG.apiKey;
        console.log('🔑 Using API key for flagging');
    } else {
        console.log('⚠️ No API key found for flagging');
    }

    // Generate a device ID for tracking
    const deviceId = `pg-admin-${Date.now()}`;

    const payload = {
        type: 'Feature',
        properties: {
            id: id,
            flag: 'duplicate',
            duplicate_of: duplicateOfId,
            updatedBy: userEmail,
            device: deviceId
        }
    };

    try {
        const response = await publicApiClient.post(url, payload, { headers });
        console.log('✅ Flag response:', response.data);
        return response.data;
    } catch (error) {
        console.error('❌ Error flagging playground:', error);
        if (error.response) {
            const enhancedError = new Error(error.response.data?.message || error.response.statusText || 'Failed to flag playground');
            enhancedError.status = error.response.status;
            throw enhancedError;
        }
        throw error;
    }
};

// Fetch installs using public API (no auth required)
// Uses caching and request deduplication to prevent excessive API calls
export const fetchPublicInstalls = async (days = 7) => {
    const url = API_CONFIG.endpoints.installs(days);
    
    // Use cached or in-flight request mechanism with longer TTL for installs (5 minutes)
    return getCachedOrInflight(url, async () => {
        console.log('🔗 Public installs URL:', url);
        console.log('🏢 Using public API base URL:', API_CONFIG.publicBaseURL);

        const headers = { 'Content-Type': 'application/json' };
        if (API_CONFIG.apiKey) {
            headers['x-api-key'] = API_CONFIG.apiKey;
            console.log('🔑 Using API key for public installs');
        } else {
            console.log('⚠️ No API key found for public installs');
        }

        try {
            const response = await publicApiClient.get(url, { headers });
            // Handle both array response and items array response
            if (Array.isArray(response.data)) {
                return response.data;
            } else if (response.data && Array.isArray(response.data.items)) {
                return response.data.items;
            }
            return response.data || [];
        } catch (error) {
            console.error('Public API failed, trying admin API:', error);
            // If public API fails, try admin API (might work without auth for this endpoint)
            try {
                const adminUrl = `${API_CONFIG.baseURL}${url}`;
                const adminResponse = await axios.get(adminUrl, { headers });
                if (Array.isArray(adminResponse.data)) {
                    return adminResponse.data;
                } else if (adminResponse.data && Array.isArray(adminResponse.data.items)) {
                    return adminResponse.data.items;
                }
                return adminResponse.data || [];
            } catch (adminError) {
                console.error('Both API attempts failed:', adminError);
                // Return empty array instead of throwing to allow page to render
                return [];
            }
        }
    }, INSTALLS_CACHE_TTL_MS);
};

// Geocode a place name to get coordinates
export const geocodePlace = async (placeName) => {
    console.log('🌍 Geocoding place:', placeName);
    
    const url = API_CONFIG.endpoints.geocodePlace(placeName);
    console.log('🔗 Geocode URL:', url);
    console.log('🏢 Using public API base URL:', API_CONFIG.publicBaseURL);
    
    return getCachedOrInflight(url, async () => {
        const headers = {
            'Content-Type': 'application/json'
        };
        
        if (API_CONFIG.apiKey) {
            headers['x-api-key'] = API_CONFIG.apiKey;
            console.log('🔑 Using API key for geocode');
        }
        
        try {
            const response = await publicApiClient.get(url, { headers });
            console.log('✅ Geocode response:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Geocode error:', error);
            throw new Error(`Failed to geocode place: ${placeName}`);
        }
    });
};

// Search playgrounds by location with type filter and radius
export const searchPlaygroundsByLocation = async (lat, lon, tp, miles = 15) => {
    console.log('🗺️ Searching playgrounds by location:', { lat, lon, tp, miles });
    
    const url = API_CONFIG.endpoints.searchByLocation(lat, lon, tp, miles);
    console.log('🔗 Search by location URL:', url);
    console.log('🏢 Using public API base URL:', API_CONFIG.publicBaseURL);
    
    return getCachedOrInflight(url, async () => {
        const headers = {
            'Content-Type': 'application/json'
        };
        
        if (API_CONFIG.apiKey) {
            headers['x-api-key'] = API_CONFIG.apiKey;
            console.log('🔑 Using API key for location search');
        }
        
        try {
            const response = await publicApiClient.get(url, { headers });
            console.log('✅ Location search response:', response.data);
            
            // Handle different response formats
            if (Array.isArray(response.data)) {
                return response.data;
            } else if (response.data && Array.isArray(response.data.features)) {
                return response.data.features;
            } else if (response.data && Array.isArray(response.data.items)) {
                return response.data.items;
            }
            return [];
        } catch (error) {
            console.error('❌ Location search error:', error);
            throw new Error(`Failed to search playgrounds by location`);
        }
    });
};

// Create a new playground item
export const createPlayground = async (playgroundData, authHeaders) => {
    console.log('➕ Creating playground:', playgroundData);
    
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.createPlayground();
        const response = await apiClient.post(url, playgroundData, { headers });
        console.log('✅ Create playground response:', response.data);
        return handleApiResponse(response);
    }, authHeaders);
};

// Update an existing playground item
export const updatePlayground = async (id, playgroundData, authHeaders) => {
    console.log('✏️ Updating playground:', id, playgroundData);
    
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.updatePlayground(id);
        const response = await apiClient.put(url, playgroundData, { headers });
        console.log('✅ Update playground response:', response.data);
        return handleApiResponse(response);
    }, authHeaders);
};

// Call AI verifier endpoint
export const callVerifier = async (id, authHeaders, enableMCP = true) => {
    console.log('🤖 Calling verifier for:', id, enableMCP ? '(with MCP enabled)' : '(MCP disabled)');
    
    return makeAuthenticatedRequest(async (headers) => {
        // URLSearchParams will automatically encode the ID properly for query string
        const url = API_CONFIG.endpoints.verifier(id, enableMCP);
        try {
            const response = await apiClient.get(url, { 
                headers,
            });
            console.log('✅ Verifier response:', response.data);
        
            // The API returns a JSON object with verificationReportUrl and agentResponse
            // We need to fetch the markdown from the S3 URL
            if (response.data && response.data.verificationReportUrl) {
                const markdownUrl = response.data.verificationReportUrl;
                // Fetch the markdown content from S3
                const markdown = await fetchMarkdownFromS3(markdownUrl);
                // Return both markdown and the verification URL
                return {
                    markdown,
                    verificationReportUrl: markdownUrl
                };
            }
            
            // Fallback: if agentResponse.text exists, use that
            if (response.data && response.data.agentResponse && response.data.agentResponse.text) {
                return {
                    markdown: response.data.agentResponse.text,
                    verificationReportUrl: response.data.verificationReportUrl || null
                };
            }
            
            // If response is a string (markdown), return it directly
            if (typeof response.data === 'string') {
                return {
                    markdown: response.data,
                    verificationReportUrl: null
                };
            }
            
            // Otherwise try to extract markdown from response object
            return {
                markdown: response.data.markdown || response.data.content || response.data,
                verificationReportUrl: response.data.verificationReportUrl || null
            };
        } catch (error) {
            // Enhanced CORS error handling
            if (error.isCorsError || (!error.response && error.message && error.message.includes('Network'))) {
                console.error('🚫 CORS error in verifier call:', error);
                throw new Error('CORS error: The server may not be properly configured to handle preflight OPTIONS requests. Please check server CORS configuration.');
            }
            throw error;
        }
    }, authHeaders);
};

// Kick off an admin-forced regeneration of AI Help for a record. This only starts the
// job (the actual generation can take minutes and is handled by a separate async Lambda
// invoke server-side) — refetch the item after a delay to see properties.AIhelp update.
export const regenerateAiHelp = async (id, authHeaders) => {
    console.log('🤖 Requesting AI help regeneration for:', id);

    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.adminAiHelp(id);
        const response = await apiClient.get(url, { headers });
        return response.data;
    }, authHeaders);
};

// Fetch markdown from S3 URL
export const fetchMarkdownFromS3 = async (s3Url) => {
    console.log('📄 Fetching markdown from S3:', s3Url);
    
    try {
        const response = await axios.get(s3Url, {
            responseType: 'text',
            timeout: API_CONFIG.timeout,
        });
        console.log('✅ Markdown fetched successfully');
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching markdown:', error);
        throw new Error(`Failed to fetch markdown from S3: ${error.message}`);
    }
};

// Upload image to playground (admin endpoint)
export const uploadImageToPlayground = async (playgroundId, fileContent, authHeaders, metadata = {}) => {
    console.log('📸 Uploading image to playground via admin endpoint:', playgroundId);
    
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.adminUploadImage ? API_CONFIG.endpoints.adminUploadImage() : API_CONFIG.endpoints.uploadImage();
        const payload = {
            id: playgroundId,
            fileContent,
            imageType: metadata.imageType || 'user'
        };

        if (metadata.center && Array.isArray(metadata.center) && metadata.center.length === 2) {
            payload.center = metadata.center;
        }
        if (metadata.description) {
            payload.description = metadata.description;
        }
        if (metadata.attribution) {
            payload.attribution = metadata.attribution;
        }
        if (typeof metadata.direction === 'number' && !Number.isNaN(metadata.direction)) {
            payload.direction = metadata.direction;
        }
        if (typeof metadata.ranking === 'number' && Number.isFinite(metadata.ranking)) {
            payload.ranking = metadata.ranking;
        }
        if (metadata.imageType) {
            payload.imageType = metadata.imageType;
        }
        if (metadata.deviceId) {
            payload.deviceId = metadata.deviceId;
        }
        if (metadata.hash) {
            payload.hash = metadata.hash;
        }
        if (metadata.embedding && Array.isArray(metadata.embedding)) {
            payload.embedding = metadata.embedding;
        }

        const response = await apiClient.post(url, payload, { headers });
        console.log('✅ Admin image upload response:', response.data);
        return handleApiResponse(response);
    }, authHeaders);
};

// Analyze image(s) using Claude AI
// NOTE: This endpoint uses /admin/analyze and requires admin authentication.
// - If imageUrl is provided, analyzes only that specific image
// - If imageUrl is omitted, analyzes all images in the item that don't already have aiAnalysis
export const analyzeImage = async (itemId, imageUrl = null, authHeaders) => {
    console.log('🔮 Analyzing image:', { itemId, imageUrl: imageUrl || 'all unanalyzed' });
    
    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.analyzeImage();
        const payload = {
            itemId: itemId
        };
        
        // If imageUrl is provided, analyze only that image
        // Otherwise, analyze all unanalyzed images
        if (imageUrl) {
            payload.imageUrl = imageUrl;
        }
        
        console.log('🌐 Attempting image analysis via admin API...');
        console.log('📋 Analyze request config:', {
            url,
            method: 'POST',
            headers,
            payload
        });
        // Use same pattern as savePlayground - explicitly stringify and ensure headers are properly merged
        const response = await apiClient.post(url, JSON.stringify(payload), { 
            headers: {
                ...headers,
                'Content-Type': 'application/json'
            }
        });
        console.log('✅ Image analysis response:', response.data);
        return handleApiResponse(response);
    }, authHeaders);
};

// Depersonalize image by removing detected people.
// Creates a new staged image and preserves the original image.
export const depersonalizeImage = async (itemId, imageUrl, authHeaders) => {
    console.log('🧼 Depersonalizing image:', { itemId, imageUrl });

    return makeAuthenticatedRequest(async (headers) => {
        const url = API_CONFIG.endpoints.depersonalizeImage();
        const payload = {
            itemId,
            imageUrl
        };

        const response = await apiClient.post(url, JSON.stringify(payload), {
            headers: {
                ...headers,
                'Content-Type': 'application/json'
            }
        });

        console.log('✅ Depersonalize response:', response.data);
        return handleApiResponse(response);
    }, authHeaders);
};